import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { initials } from '../utils/format';
import { useToast } from '../context/ToastContext';
import PageHeader from '../components/ui/PageHeader';

const ROLE_COLOR = {
  ADMIN: 'bg-brand-50 text-brand-700',
  STAFF: 'bg-info-50 text-info-700',
  VIEWER: 'bg-surface-hover text-content-muted',
};

export default function Profile() {
  const { user, memberships, currentCompany, companyId, selectCompany, logout } = useAuth();
  const { success } = useToast();
  const nav = useNavigate();

  const switchCompany = async (cid) => {
    await selectCompany(cid);
    success('Switched company.');
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <PageHeader icon={User} eyebrow="Account" title="My Profile" subtitle="Your details and company memberships" />

      {/* Identity card */}
      <div className="card flex items-center gap-4 p-6">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-700 text-2xl font-bold text-white">
          {initials(user?.name)}
        </div>
        <div className="min-w-0">
          <p className="text-xl font-bold text-content">{user?.name}</p>
          <p className="text-sm text-content-subtle">Signed in to {currentCompany?.name || 'Bespoke Tailors'}</p>
        </div>
      </div>

      {/* Companies */}
      <div className="card p-5">
        <h3 className="mb-3 font-display text-lg font-semibold text-brand-900">My Companies</h3>
        <div className="space-y-2">
          {memberships.map((m) => {
            const active = m.company.id === Number(companyId);
            return (
              <div key={m.company.id} className={`flex items-center justify-between rounded-xl border px-4 py-3 ${active ? 'border-brand-300 bg-brand-50' : 'border-line'}`}>
                <div>
                  <p className="font-medium text-content">{m.company.name}</p>
                  <p className="text-xs text-content-subtle">{m.company.gstin || '—'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ROLE_COLOR[m.role] || ROLE_COLOR.VIEWER}`}>{m.role}</span>
                  {active ? (
                    <span className="text-xs font-semibold text-success-600">● Active</span>
                  ) : (
                    <button className="btn-outline !py-1 text-xs" onClick={() => switchCompany(m.company.id)}>Switch</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick actions */}
      <div className="card p-5">
        <h3 className="mb-3 font-display text-lg font-semibold text-brand-900">Account</h3>
        <div className="flex flex-wrap gap-2">
          <button className="btn-outline" onClick={() => nav('/change-password')}>Change Password</button>
          <button className="btn-danger" onClick={logout}>Sign Out</button>
        </div>
      </div>
    </div>
  );
}
